import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import ResolveEntitiesModalMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { useHilSegmentAttentionResolve } from 'app/pages/HumanInTheLoopPage/hooks/useHilSegmentAttentionResolve';
import HilSegmentAttentionProgressLayer from 'app/pages/HumanInTheLoopPage/components/HilSegmentAttentionProgressLayer';
import SegmentResolveResponseModal from 'app/pages/HumanInTheLoopPage/components/SegmentResolveResponseModal';
import type { HilOpportunityType } from 'app/pages/HumanInTheLoopPage/types';
import type { HilSegmentAttentionActionsBridge } from 'app/pages/HumanInTheLoopPage/utils/hilSegmentAttentionActionsBridge';
import type { Ref, RefObject } from 'react';
import type { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';

function toUiEventLabel(eventName: string): string {
  return eventName
    .replace(/^nurturing_test_automation_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

type HilSegmentsAttentionOverlaysProps = {
  dealerId: string;
  canAccess: boolean;
  opportunityType: HilOpportunityType;
  functionalSegmentsCount: number;
  onRefreshGrid: () => void;
  dataGridRef: RefObject<DataGridMAIQRef>;
  actionsBridgeRef: RefObject<HilSegmentAttentionActionsBridge>;
  onToolbarLoadingChange: (state: {
    retryAllLoading: boolean;
    resolveActionLoading: boolean;
  }) => void;
};

export default function HilSegmentsAttentionOverlays({
  dealerId,
  canAccess,
  opportunityType,
  functionalSegmentsCount,
  onRefreshGrid,
  dataGridRef,
  actionsBridgeRef,
  onToolbarLoadingChange,
}: HilSegmentsAttentionOverlaysProps) {
  const segmentResolve = useHilSegmentAttentionResolve({
    dealerId,
    canAccess,
    opportunityType,
    functionalSegmentsCount,
    onRefreshGrid,
    dataGridRef,
  });

  useLayoutEffect(() => {
    const bridge = actionsBridgeRef.current;
    if (!bridge) return;
    bridge.handleCellClick = segmentResolve.handleCellClick;
    bridge.openRetryAllConfirm = segmentResolve.openRetryAllConfirm;
  }, [
    actionsBridgeRef,
    segmentResolve.handleCellClick,
    segmentResolve.openRetryAllConfirm,
  ]);

  useEffect(() => {
    onToolbarLoadingChange({
      retryAllLoading: segmentResolve.retryAllLoading,
      resolveActionLoading: segmentResolve.resolveActionLoading,
    });
  }, [
    onToolbarLoadingChange,
    segmentResolve.resolveActionLoading,
    segmentResolve.retryAllLoading,
  ]);

  const [resolveDetailsOpen, setResolveDetailsOpen] = useState(false);

  const latestResolveSocketMessage = useMemo(
    () =>
      segmentResolve.automationSocketMessages.length > 0
        ? segmentResolve.automationSocketMessages[
            segmentResolve.automationSocketMessages.length - 1
          ]
        : null,
    [segmentResolve.automationSocketMessages],
  );

  useEffect(() => {
    if (segmentResolve.resolveApiResponseBody) {
      setResolveDetailsOpen(true);
    }
  }, [segmentResolve.resolveResponseVersion, segmentResolve.resolveApiResponseBody]);

  return (
    <>
      <HilSegmentAttentionProgressLayer
        automationInProgress={segmentResolve.automationInProgress}
        showResolveProgress={segmentResolve.showResolveProgress}
        retryAllInProgress={segmentResolve.retryAllInProgress}
        automationStreamingDataForCard={segmentResolve.automationStreamingDataForCard}
        automationCurrentSectionForCard={segmentResolve.automationCurrentSectionForCard}
        automationProgressPercentage={segmentResolve.automationProgressPercentage}
        automationProgressCurrent={segmentResolve.automationProgressCurrent}
        automationProgressTotal={segmentResolve.automationProgressTotal}
        automationLatestStatusText={segmentResolve.automationLatestStatusText}
        automationLatestStepLabel={segmentResolve.automationLatestStepLabel}
        automationLastUpdatedAt={segmentResolve.automationLastUpdatedAt}
      />

      <ResolveEntitiesModalMAIQ
        open={segmentResolve.resolveModalOpen}
        onClose={segmentResolve.closeResolveModal}
        dealerId={dealerId}
        row={segmentResolve.resolveModalRow}
        onSuccess={segmentResolve.handleResolveModalSuccess}
        isResolving={segmentResolve.resolveActionLoading}
        onResolveClick={() => {
          void segmentResolve.resolveRow(segmentResolve.resolveModalRow);
        }}
      />

      <SegmentResolveResponseModal
        open={resolveDetailsOpen}
        onClose={() => setResolveDetailsOpen(false)}
        body={segmentResolve.resolveApiResponseBody}
        showProgress={segmentResolve.showResolveProgress}
        progressCurrent={segmentResolve.automationProgressCurrent}
        progressTotal={segmentResolve.automationProgressTotal}
        progressPercentage={segmentResolve.automationProgressPercentage}
        latestEventLabel={
          latestResolveSocketMessage
            ? toUiEventLabel(latestResolveSocketMessage.eventName)
            : ''
        }
      />

      <ConfirmationDialogMAIQ
        open={segmentResolve.retryAllConfirmOpen}
        onClose={segmentResolve.closeRetryAllConfirm}
        onConfirm={() => {
          void segmentResolve.confirmRetryAll();
        }}
        title="Retry all segments"
        message={`Retry all ${functionalSegmentsCount} functional segment(s) needing attention?`}
        confirmLabel="Retry All"
        loading={segmentResolve.retryAllLoading}
        loadingLabel="Retrying..."
      />
    </>
  );
}
